/**
 * EntityActivityPanel - Generic Activity/History sidebar panel
 * 
 * Shows "who did what at what time" for any entity (Orders, POs, Vendors, Bills).
 * Reusable component with entity-specific configuration.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { activityLogsAPI } from '../../utils/api';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Clock,
  FileText,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Loader2,
  RefreshCw,
  ChevronRight,
  User,
  Zap,
  Send,
  Package,
  Ban,
} from 'lucide-react';

// Action type configurations (all entity types)
const ACTION_CONFIG = {
  // Order actions
  ORDER_CREATED: {
    label: 'Order Created',
    icon: Zap,
    color: 'text-green-600',
    bgColor: 'bg-green-100',
  },
  ORDER_STATUS_CHANGED: {
    label: 'Status Changed',
    icon: RefreshCw,
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
  },
  INVOICE_ISSUED: {
    label: 'Invoice Issued',
    icon: FileText,
    color: 'text-purple-600',
    bgColor: 'bg-purple-100',
  },
  INVOICE_VOIDED: {
    label: 'Invoice Voided',
    icon: XCircle,
    color: 'text-red-600',
    bgColor: 'bg-red-100',
  },
  QUOTE_ISSUED: {
    label: 'Quote Issued',
    icon: FileText,
    color: 'text-teal-600',
    bgColor: 'bg-teal-100',
  },
  PAYMENT_APPLIED: {
    label: 'Payment Applied',
    icon: CheckCircle,
    color: 'text-green-600',
    bgColor: 'bg-green-100',
  },
  // Reminder actions
  REMINDER_SENT: {
    label: 'Payment Reminder Sent',
    icon: Send,
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
  },
  REMINDER_FAILED: {
    label: 'Reminder Failed',
    icon: AlertTriangle,
    color: 'text-red-600',
    bgColor: 'bg-red-100',
  },
  // PO actions
  PO_CREATED: {
    label: 'PO Created',
    icon: Zap,
    color: 'text-green-600',
    bgColor: 'bg-green-100',
  },
  PO_SENT: {
    label: 'PO Sent to Vendor',
    icon: Send,
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
  },
  PO_CANCELLED: {
    label: 'PO Cancelled',
    icon: Ban,
    color: 'text-red-600',
    bgColor: 'bg-red-100',
  },
  PO_CLOSED: {
    label: 'PO Closed',
    icon: CheckCircle,
    color: 'text-green-600',
    bgColor: 'bg-green-100',
  },
  BILL_AUTO_CREATED: {
    label: 'Draft Bill Auto-Created',
    icon: FileText,
    color: 'text-purple-600',
    bgColor: 'bg-purple-100',
  },
  // Bill actions
  BILL_CREATED: {
    label: 'Bill Created',
    icon: Zap,
    color: 'text-green-600',
    bgColor: 'bg-green-100',
  },
  BILL_FINALIZED: {
    label: 'Bill Finalized',
    icon: CheckCircle,
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
  },
  BILL_REOPENED: {
    label: 'Bill Reopened to Draft',
    icon: RefreshCw,
    color: 'text-amber-600',
    bgColor: 'bg-amber-100',
  },
  BILL_VOIDED: {
    label: 'Bill Voided',
    icon: XCircle,
    color: 'text-red-600',
    bgColor: 'bg-red-100',
  },
  BILL_SYNCED_FROM_PO: {
    label: 'Bill Synced from PO',
    icon: RefreshCw,
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
  },
  BILL_PAID: {
    label: 'Bill Paid',
    icon: CheckCircle,
    color: 'text-green-600',
    bgColor: 'bg-green-100',
  },
};

// Default config for unknown actions
const DEFAULT_ACTION_CONFIG = {
  label: 'Activity',
  icon: Clock,
  color: 'text-gray-600',
  bgColor: 'bg-gray-100',
};

// Format relative time
const formatRelativeTime = (timestamp) => {
  const now = new Date();
  const date = new Date(timestamp);
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
};

// Format full datetime
const formatFullDateTime = (timestamp) => {
  return new Date(timestamp).toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

// Single activity item - Clean human-readable format
const ActivityItem = ({ log, showFullDate = false }) => {
  const config = ACTION_CONFIG[log.action] || DEFAULT_ACTION_CONFIG;
  const Icon = config.icon;
  
  // Format the date in a clean way
  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
    }) + ' at ' + date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };
  
  // Get clean user display name
  const getUserName = (actorName) => {
    if (!actorName) return 'System';
    if (actorName === 'System') return 'System';
    // Extract name from email
    const name = actorName.split('@')[0];
    // Capitalize first letter
    return name.charAt(0).toUpperCase() + name.slice(1);
  };
  
  return (
    <div className="flex gap-3 py-3 border-b border-gray-100 last:border-0" data-testid={`activity-item-${log.id}`}>
      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${config.bgColor}`}>
        <Icon className={`w-4 h-4 ${config.color}`} />
      </div>
      <div className="flex-1 min-w-0 space-y-1">
        {/* Action label */}
        <p className="text-sm font-medium text-gray-900">{config.label}</p>
        
        {/* Summary - human readable description */}
        {log.summary && (
          <p className="text-xs text-gray-600">{log.summary}</p>
        )}
        
        {/* Old → New values (if present) */}
        {log.old_value && log.new_value && (
          <div className="flex items-center gap-1.5 mt-1.5">
            <Badge variant="outline" className="text-xs px-1.5 py-0 bg-gray-50">{log.old_value}</Badge>
            <ChevronRight className="w-3 h-3 text-gray-400" />
            <Badge className="text-xs px-1.5 py-0 bg-blue-100 text-blue-700">{log.new_value}</Badge>
          </div>
        )}
        
        {/* Meta: Date and User */}
        <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-400">
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {showFullDate ? formatFullDateTime(log.at) : formatDate(log.at)}
          </span>
          <span className="flex items-center gap-1">
            <User className="w-3 h-3" />
            {getUserName(log.actor_name)}
          </span>
        </div>
      </div>
    </div>
  );
};

// Main component
const EntityActivityPanel = ({ 
  entityType, // ORDER, PURCHASE_ORDER, VENDOR, BILL
  entityId,
  title = 'Activity',
  displayLimit = 4  // Show only 4 recent logs by default
}) => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAllModal, setShowAllModal] = useState(false);
  const [allLogs, setAllLogs] = useState([]);
  const [loadingAll, setLoadingAll] = useState(false);
  
  // Fetch recent logs
  const fetchLogs = useCallback(async () => {
    if (!entityId || !entityType) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const data = await activityLogsAPI.getEntityLogs(entityType, entityId, displayLimit);
      setLogs(data.logs || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Failed to fetch activity logs:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [entityId, entityType, displayLimit]);
  
  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);
  
  // Fetch all logs for modal
  const fetchAllLogs = async () => {
    setLoadingAll(true);
    try {
      const data = await activityLogsAPI.getEntityLogs(entityType, entityId, 100);
      setAllLogs(data.logs || []);
    } catch (err) {
      console.error('Failed to fetch all activity logs:', err);
    } finally {
      setLoadingAll(false);
    }
  };
  
  const handleViewAll = () => {
    setShowAllModal(true);
    fetchAllLogs();
  };
  
  if (loading && logs.length === 0) {
    return (
      <div className="p-4 text-center">
        <Loader2 className="w-5 h-5 animate-spin mx-auto text-gray-400" />
      </div>
    );
  }
  
  return (
    <div className="bg-white rounded-xl border" data-testid={`${entityType.toLowerCase()}-activity-panel`}>
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <h3 className="font-semibold text-sm flex items-center gap-2">
          <Clock className="w-4 h-4 text-gray-500" />
          {title}
          {total > 0 && (
            <Badge variant="outline" className="text-xs px-1.5">{total}</Badge>
          )}
        </h3>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={fetchLogs}
          className="h-7 w-7 p-0"
          title="Refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>
      
      <div className="px-4 py-2">
        {error ? (
          <div className="text-center py-4">
            <AlertTriangle className="w-5 h-5 text-amber-500 mx-auto mb-1" />
            <p className="text-xs text-gray-500">Failed to load activity</p>
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-6">
            <Clock className="w-6 h-6 text-gray-300 mx-auto mb-1" />
            <p className="text-xs text-gray-400">No activity yet</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {logs.map((log) => (
              <ActivityItem key={log.id} log={log} />
            ))}
          </div>
        )}
      </div>
      
      {total > displayLimit && (
        <div className="px-4 py-2 border-t">
          <Button 
            variant="link" 
            size="sm" 
            className="w-full text-blue-600 hover:text-blue-700"
            onClick={handleViewAll}
            data-testid="view-all-activity-btn"
          >
            View all {total} activities
          </Button>
        </div>
      )}
      
      {/* View All Modal */}
      <Dialog open={showAllModal} onOpenChange={setShowAllModal}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              {title} History
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-2">
            {loadingAll ? (
              <div className="text-center py-8">
                <Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" />
                <p className="text-sm text-gray-500 mt-2">Loading...</p>
              </div>
            ) : allLogs.length === 0 ? (
              <div className="text-center py-8">
                <Clock className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-500">No activity recorded</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {allLogs.map((log) => (
                  <ActivityItem key={log.id} log={log} showFullDate />
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EntityActivityPanel;
